/**
 * Long-press the undo key, then slide to choose how many balls go back.
 *
 * The state machine only. No pointer events, no timers, no DOM — those are three lines in
 * `Pad.tsx`, and everything worth being wrong about is here, where it can be tested. There is
 * no DOM-rendering test infrastructure in this app at all, so a gesture whose logic lived in
 * the component would be a gesture nothing checked.
 *
 * **This is an accelerator, never the only way.** `styles.css` says outright that there are no
 * swipe-only gestures anywhere, and a hold-and-slide is exactly the kind of thing a scorer
 * cannot discover under pressure. Tapping the key still undoes one ball, and undoing three is
 * three taps — the scrub saves time for someone who already knows it is there and costs nothing
 * to someone who does not.
 *
 * Two numbers are chosen rather than derived, and both are chosen against the hand:
 *
 * - **The hold is 400ms.** Long enough that a fast tap during an over is never mistaken for the
 *   start of a scrub — the failure that would matter, because it turns "undo one ball" into a
 *   dialog the scorer did not ask for. Short enough not to feel stuck.
 * - **A ball every 28px.** A thumb travelling the width of a phone reaches about a dozen, which
 *   is the range that is any use; finer than this and a sweaty finger cannot land on a number,
 *   coarser and reaching six balls means leaving the screen.
 */

/** How long the key must be held before a slide means anything. */
export const HOLD_MS = 400;

/** Pixels of travel per additional ball. */
export const PX_PER_BALL = 28;

export interface Scrub {
  /** True once the hold threshold has passed. Before that a release is an ordinary tap. */
  readonly active: boolean;
  /** How many balls a release would undo. Always at least 1 while active. */
  readonly count: number;
}

export const NO_SCRUB: Scrub = { active: false, count: 0 };

/**
 * The hold threshold has passed. One ball to start with, which is what a tap would have done.
 *
 * Starting at 1 rather than 0 means that a hold the scorer did not mean to make, released
 * without moving, does what he would have got by tapping — so the accidental case costs nothing
 * to recognise and nothing to recover from.
 */
export const beganScrub = (): Scrub => ({ active: true, count: 1 });

/**
 * Where the slide has reached.
 *
 * `travelled` is distance from where the finger went down, in pixels, and only the magnitude
 * counts. Direction is deliberately ignored: "which way is more" has no natural answer on a key
 * that sits at the bottom of the screen, and a scorer who slides the wrong way would otherwise
 * get nothing and conclude the gesture is broken.
 */
export function scrubbedTo(scrub: Scrub, travelled: number, available: number): Scrub {
  if (!scrub.active) return scrub;
  const wanted = 1 + Math.floor(Math.abs(travelled) / PX_PER_BALL);
  // Never past the start of the innings: a scrub that offered to undo eight balls when six were
  // bowled would be offering something the ledger cannot do, and the number on screen is the
  // scorer's only warning about how much is about to go.
  return { active: true, count: Math.max(1, Math.min(wanted, Math.max(available, 1))) };
}

/**
 * What a release should undo, or null if nothing should happen.
 *
 * Null when there is nothing to undo, so a release over an empty innings is silent rather than
 * a no-op the scorer has to interpret.
 */
export function releasedAt(scrub: Scrub, available: number): number | null {
  if (!scrub.active || available === 0) return null;
  return Math.min(scrub.count, available);
}

/** What the key says while scrubbing. Plural because "undo 1 balls" is the tell of a rushed UI. */
export const scrubLabel = (count: number): string =>
  count === 1 ? "Undo 1 ball" : `Undo ${count} balls`;
